import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { Star } from "@/components/landing/Logo";
import { useLang } from "@/i18n/LanguageContext";

const COLLECTIONS = {
  spring: ["fruehlings-capsule-wardrobe", "farbtyp-bestimmen-ki", "fashion-must-haves-frauen"],
  summer: ["herrenmode-trends-outfit-themen", "dating-outfit-maenner", "7-tage-glow-up-routine"],
  autumn: ["herbst-capsule-wardrobe", "fashion-must-haves-maenner", "welcher-stiltyp-bin-ich"],
  winter: ["herren-capsule-wardrobe", "business-outfit-wirkung", "stil-verbessern-ohne-shopping"],
  festive: ["dresscode-guide-maenner", "hochzeitsgast-outfit-farben", "outfit-vergleich-entscheiden"],
};

const SEASON_CYCLE = ["spring", "summer", "autumn", "winter"];

export const currentSeason = (d = new Date()) => {
  const m = d.getMonth() + 1;
  if (m >= 3 && m <= 5) return "spring";
  if (m >= 6 && m <= 8) return "summer";
  if (m >= 9 && m <= 11) return "autumn";
  return "winter";
};

const TEXTS = {
  de: {
    kicker: "Saison-Edit",
    title: "Guides zur Saison",
    sub: "Kuratiert für die Monate, in denen dein Schrank am meisten arbeiten muss.",
    now: "Jetzt",
    seasons: {
      spring: { name: "Frühling", line: "Frische Farben, leichte Teile & der Neustart im Schrank" },
      summer: { name: "Sommer", line: "Riviera-Looks, Dates & Glow für lange Tage" },
      autumn: { name: "Herbst", line: "Trench, Layer & die Capsule für goldene Tage" },
      winter: { name: "Winter", line: "Mantel, Strick & starke Looks für dunkle Tage" },
      festive: { name: "Festtage", line: "Dresscodes, Hochzeiten & der sichere Auftritt" },
    },
  },
  en: {
    kicker: "The Season Edit",
    title: "Guides for the Season",
    sub: "Curated for the months when your wardrobe has to work hardest.",
    now: "Now",
    seasons: {
      spring: { name: "Spring", line: "Fresh colours, light pieces & the wardrobe restart" },
      summer: { name: "Summer", line: "Riviera looks, dates & glow for long days" },
      autumn: { name: "Autumn", line: "Trench, layers & the capsule for golden days" },
      winter: { name: "Winter", line: "Coat, knitwear & strong looks for dark days" },
      festive: { name: "Festive Days", line: "Dress codes, weddings & the confident entrance" },
    },
  },
  es: {
    kicker: "Edición de temporada",
    title: "Guías para la temporada",
    sub: "Curadas para los meses en que tu armario más tiene que rendir.",
    now: "Ahora",
    seasons: {
      spring: { name: "Primavera", line: "Colores frescos, piezas ligeras y el reinicio del armario" },
      summer: { name: "Verano", line: "Looks riviera, citas y glow para días largos" },
      autumn: { name: "Otoño", line: "Trench, capas y la cápsula para días dorados" },
      winter: { name: "Invierno", line: "Abrigo, punto y looks fuertes para días oscuros" },
      festive: { name: "Fiestas", line: "Dress codes, bodas y la entrada segura" },
    },
  },
  it: {
    kicker: "Season Edit",
    title: "Guide per la stagione",
    sub: "Curate per i mesi in cui il tuo guardaroba deve lavorare di più.",
    now: "Ora",
    seasons: {
      spring: { name: "Primavera", line: "Colori freschi, capi leggeri e il restart del guardaroba" },
      summer: { name: "Estate", line: "Look riviera, appuntamenti e glow per giornate lunghe" },
      autumn: { name: "Autunno", line: "Trench, layer e la capsule per i giorni dorati" },
      winter: { name: "Inverno", line: "Cappotto, maglieria e look forti per i giorni bui" },
      festive: { name: "Feste", line: "Dress code, matrimoni e l'ingresso sicuro" },
    },
  },
  fr: {
    kicker: "L'édit de saison",
    title: "Guides de saison",
    sub: "Une curation pour les mois où ta garde-robe doit travailler le plus.",
    now: "En ce moment",
    seasons: {
      spring: { name: "Printemps", line: "Couleurs fraîches, pièces légères et le redémarrage de l'armoire" },
      summer: { name: "Été", line: "Looks riviera, rendez-vous et glow pour les longues journées" },
      autumn: { name: "Automne", line: "Trench, layers et la capsule des jours dorés" },
      winter: { name: "Hiver", line: "Manteau, maille et looks forts pour les jours sombres" },
      festive: { name: "Fêtes", line: "Dress codes, mariages et l'entrée assurée" },
    },
  },
};

export const SeasonEdit = ({ posts }) => {
  const { lang } = useLang();
  const x = TEXTS[lang] || TEXTS.de;
  const bySlug = Object.fromEntries(posts.map((p) => [p.slug, p]));

  const season = currentSeason();
  const next = SEASON_CYCLE[(SEASON_CYCLE.indexOf(season) + 1) % 4];
  const order = [season, next, "festive"];

  return (
    <section className="mx-auto mt-12 max-w-7xl px-5 sm:px-8" data-testid="season-edit">
      <Reveal>
        <div className="overflow-hidden rounded-[2rem] px-7 py-10 sm:px-12 sm:py-12" style={{ background: "var(--on-surface)" }}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-[11px] font-black uppercase" style={{ letterSpacing: "0.24em", color: "var(--gold)" }}>
                <Star size={11} /> {x.kicker}
              </p>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl font-semibold text-white" data-testid="season-edit-title">{x.title}</h2>
            </div>
            <p className="max-w-xs text-sm" style={{ color: "rgba(252,250,248,0.55)" }}>{x.sub}</p>
          </div>

          <div className="mt-9 grid grid-cols-1 gap-9 md:grid-cols-3 md:gap-0 md:divide-x" style={{ borderColor: "rgba(252,250,248,0.14)" }}>
            {order.map((key, si) => (
              <div key={key} className="md:px-8 md:first:pl-0 md:last:pr-0" data-testid={`season-col-${key}`}>
                <p className="flex items-center gap-2.5 font-serif-display text-xl font-semibold italic" style={{ color: "var(--camel)" }}>
                  <span style={{ color: "var(--gold)" }}>{["I", "II", "III"][si]}</span> · {x.seasons[key].name}
                  {si === 0 && (
                    <span className="rounded-full border px-2.5 py-0.5 font-sans text-[9px] font-black uppercase not-italic" style={{ letterSpacing: "0.16em", color: "var(--gold)", borderColor: "rgba(212,175,55,0.5)" }} data-testid="season-now-badge">
                      {x.now}
                    </span>
                  )}
                </p>
                <p className="mt-1.5 text-xs" style={{ color: "rgba(252,250,248,0.5)" }}>{x.seasons[key].line}</p>
                <ul className="mt-5 space-y-3.5">
                  {COLLECTIONS[key].map((slug) => {
                    const p = bySlug[slug];
                    if (!p) return null;
                    return (
                      <li key={slug}>
                        <Link
                          to={`/blog/${slug}`}
                          data-testid={`season-link-${slug}`}
                          className="group flex items-start gap-2.5 text-sm font-medium leading-snug text-white/85 transition-colors duration-300 hover:text-[var(--gold)]"
                        >
                          <ArrowRight size={14} className="mt-0.5 shrink-0 transition-transform duration-300 group-hover:translate-x-1" style={{ color: "var(--camel)" }} />
                          {p.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
};
